"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Registers the offline service worker (/app-sw.js — inc/app/app-sw.php).
 *
 * The server prints <meta name="prepain-sw"> only where the worker should be
 * registered (main site, app surfaces, kill switch off), so this module is a
 * no-op everywhere else. Registration waits for window load so it never
 * competes with page resources; failures are silently ignored (the app works
 * identically without the worker — it only adds the offline fallback and
 * asset revalidation).
 *
 * Also keeps downloaded lessons (blocks/video/video-downloads.js) with the
 * student who saved them: they are removed when <meta name="prepain-user">
 * names another student, and when the student taps "Cerrar sesión". Not
 * when it says 0 (nobody): a session that simply expired — a week offline,
 * exactly who downloads are for — must find them there after signing in.
 */
const DOWNLOADS_PREFIX = 'prepain-descargas';
const DOWNLOADS_OWNER = 'prepain-descargas-user';
class AppSW {
    init() {
        this.forgetOthersDownloads();
        this.forgetOnSignOut();
        const meta = document.querySelector('meta[name="prepain-sw"]');
        if (!meta || !meta.content || !('serviceWorker' in navigator)) {
            return;
        }
        const url = meta.content;
        if (document.readyState === 'complete') {
            this.register(url);
        }
        else {
            window.addEventListener('load', () => this.register(url));
        }
    }
    register(url) {
        navigator.serviceWorker.register(url, { scope: '/' }).catch(() => {
            // Registration can fail on old browsers or flaky networks — never fatal.
        });
    }
    forgetOthersDownloads() {
        const user = document.querySelector('meta[name="prepain-user"]');
        let owner = null;
        try {
            owner = window.localStorage.getItem(DOWNLOADS_OWNER);
        }
        catch (e) {
            return;
        }
        if (!user || !owner || user.content === '0' || owner === user.content) {
            return;
        }
        this.forgetDownloads();
    }
    // Sign-out links (wp_logout_url): remove the downloads, then leave.
    forgetOnSignOut() {
        document.addEventListener('click', (event) => {
            var _a, _b;
            const link = (_b = (_a = event.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, 'a[href*="action=logout"]');
            if (!link || !('caches' in window) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }
            event.preventDefault();
            const leave = () => window.location.assign(link.href);
            // Never keep the student from signing out: go after 1.5 s regardless.
            Promise.race([this.forgetDownloads(), new Promise((resolve) => window.setTimeout(resolve, 1500))]).then(leave, leave);
        });
    }
    forgetDownloads() {
        if (!('caches' in window)) {
            return Promise.resolve();
        }
        return caches
            .keys()
            .then((keys) => Promise.all(keys
            .filter((key) => key.indexOf(DOWNLOADS_PREFIX) === 0)
            .map((key) => caches.delete(key))))
            .then(() => {
            try {
                window.localStorage.removeItem(DOWNLOADS_OWNER);
            }
            catch (e) {
                // Nothing left to protect: the caches are gone.
            }
        })
            .catch(() => { });
    }
}
exports.default = AppSW;
